import type {AssistantMemoryResponse} from 'src/api/ProactApiClient';
import {
  AssistantMemoryKindEnum,
  AssistantMemorySourceEnum,
  AssistantMemoryStatusEnum,
} from 'src/api/ProactApiClient';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {ISelectOption} from 'src/types/Form';

/** Mirrors AssistantMemory.MaxTextLength on the server. */
export const MEMORY_TEXT_MAX_LENGTH = 200;

const STALE_AFTER_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const KIND_LABELS: Record<AssistantMemoryKindEnum, string> = {
  [AssistantMemoryKindEnum.Preference]: 'Preference',
  [AssistantMemoryKindEnum.Focus]: 'Focus',
  [AssistantMemoryKindEnum.Alias]: 'Alias',
  [AssistantMemoryKindEnum.Default]: 'Default',
};

const SOURCE_LABELS: Record<AssistantMemorySourceEnum, string> = {
  [AssistantMemorySourceEnum.UserRequest]: 'Asked in chat',
  [AssistantMemorySourceEnum.Suggestion]: 'Suggested by Scout',
  [AssistantMemorySourceEnum.Manual]: 'Added here',
};

export const memoryKindLabel = (kind: AssistantMemoryKindEnum): string =>
  KIND_LABELS[kind] ?? `Kind ${kind}`;

export const memorySourceLabel = (source: AssistantMemorySourceEnum): string =>
  SOURCE_LABELS[source] ?? `Source ${source}`;

const KIND_HELP: Record<
  AssistantMemoryKindEnum,
  {description: string; example: string}
> = {
  [AssistantMemoryKindEnum.Preference]: {
    description:
      'How Scout answers: tone, length, units, or layout. Used in every answer.',
    example: 'Show durations in hours, not days.',
  },
  [AssistantMemoryKindEnum.Focus]: {
    description:
      'What you work on. Used to choose what comes first when you ask a broad question.',
    example: 'Works on the North Yard project.',
  },
  [AssistantMemoryKindEnum.Alias]: {
    description:
      'Your own name or term for a site, project, or other thing. Used when you use that name.',
    example: 'TRIR means total recordable incident rate.',
  },
  [AssistantMemoryKindEnum.Default]: {
    description:
      'A filter to assume when you do not give one. Scout says when it applies it.',
    example: 'Assume the North Yard project.',
  },
};

export const memoryKindDescription = (kind: AssistantMemoryKindEnum): string =>
  KIND_HELP[kind]?.description ?? '';

export const memoryKindExample = (kind: AssistantMemoryKindEnum): string =>
  `For example: ${KIND_HELP[kind]?.example ?? KIND_HELP[AssistantMemoryKindEnum.Preference].example}`;

export const MEMORY_KIND_OPTIONS: ISelectOption[] = [
  AssistantMemoryKindEnum.Preference,
  AssistantMemoryKindEnum.Focus,
  AssistantMemoryKindEnum.Alias,
  AssistantMemoryKindEnum.Default,
].map(kind => ({
  id: kind,
  label: KIND_LABELS[kind],
  description: KIND_HELP[kind].description,
}));

export const isProposedMemory = (note: AssistantMemoryResponse): boolean =>
  note.status === AssistantMemoryStatusEnum.Proposed;

/** An active note neither used nor created in the last 30 days. */
export const isStaleMemory = (
  note: AssistantMemoryResponse,
  now: Date = new Date()
): boolean => {
  if (isProposedMemory(note)) return false;
  const reference = note.lastUsedAt ?? note.createdAt;
  return (
    now.getTime() - parseUtcDate(reference).getTime() > STALE_AFTER_DAYS * DAY_MS
  );
};
